import { AnchorHTMLAttributes, ReactNode } from 'react';
import { icons } from 'lucide-react';

type ButtonVariant = 'primary' | 'secondary' | 'outline';
type ButtonSize = 'sm' | 'md';
type IconName = keyof typeof icons;

interface ButtonProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  text: string;
  subtext?: string;
  link: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  subtextAlignment?: 'center' | 'left';
  iconBefore?: IconName | ReactNode;
  iconAfter?: IconName | ReactNode;
  disabled?: boolean;
}

const baseClasses =
  'inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-semibold transition focus:outline-none focus:ring-0';

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: '',
};

const sizeFontPx: Record<ButtonSize, number> = {
  sm: 12,
  md: 16,
};

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'text-white bg-talo-blue hover:bg-talo-blue-600 focus:bg-talo-blue-800 aria-disabled:bg-talo-blue aria-disabled:bg-opacity-60',
  secondary:
    'text-gray-900 bg-salmon-400 hover:bg-salmon-600 hover:text-white focus:bg-salmon-800 aria-disabled:bg-salmon-400 aria-disabled:opacity-70',
  outline:
    'text-talo-blue bg-transparent border border-talo-blue hover:bg-talo-blue-100 focus:bg-transparent aria-disabled:opacity-60',
};

const toPascalCase = (name: string) =>
  name
    .toLowerCase()
    .split(/[\s-_]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

const renderIcon = (icon?: IconName | ReactNode) => {
  if (!icon) return null;
  if (typeof icon === 'string') {
    const pascalName = toPascalCase(icon);
    const LucideIcon = icons[pascalName as IconName] || icons[icon as IconName];
    if (!LucideIcon) return null;
    return <LucideIcon className="h-4 w-4" aria-hidden />;
  }
  return icon;
};

const Button = ({
  text,
  subtext,
  link,
  variant = 'primary',
  size = 'md',
  subtextAlignment = 'center',
  iconBefore,
  iconAfter,
  className = '',
  disabled,
  onClick,
  ...rest
}: ButtonProps) => {
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;
  const { style, ...restProps } = rest;
  const isDisabled = disabled || !link;
  const stackedTextClasses =
    subtextAlignment === 'left'
      ? 'flex flex-col items-start text-left leading-tight'
      : 'flex flex-col items-center leading-tight';

  return (
    <a
      className={`${classes} ${isDisabled ? 'pointer-events-none' : ''}`}
      href={isDisabled ? undefined : link}
      aria-disabled={isDisabled}
      onClick={(event) => {
        if (isDisabled) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }}
      style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontWeight: 600,
        fontSize: `${sizeFontPx[size]}px`,
        ...(style || {}),
      }}
      {...restProps}
    >
      {renderIcon(iconBefore) && <span className="flex items-center">{renderIcon(iconBefore)}</span>}
      <span className={subtext ? stackedTextClasses : ''}>
        <span>{text}</span>
        {subtext && <span className="text-xs font-medium opacity-80">{subtext}</span>}
      </span>
      {renderIcon(iconAfter) && <span className="flex items-center">{renderIcon(iconAfter)}</span>}
    </a>
  );
};

export default Button;
